import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, User, Phone, Mail, MapPin, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { createOrder, sendWhatsAppOrder } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Checkout = () => {
    const { cartItems, getCartTotal, clearCart } = useCart();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [orderData, setOrderData] = useState(null);

    const [formData, setFormData] = useState({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerAddress: '',
        notes: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.customerName.trim()) {
            newErrors.customerName = 'Name is required';
        }

        if (!formData.customerPhone.trim()) {
            newErrors.customerPhone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(formData.customerPhone.replace(/\s/g, ''))) {
            newErrors.customerPhone = 'Please enter a valid 10-digit phone number';
        }

        if (formData.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
            newErrors.customerEmail = 'Please enter a valid email address';
        }

        if (!formData.customerAddress.trim()) {
            newErrors.customerAddress = 'Delivery address is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const orderPayload = {
                ...formData,
                items: cartItems.map(item => ({
                    productId: item.id,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price
                })),
                totalAmount: getCartTotal()
            };

            const res = await createOrder(orderPayload);

            if (res.success) {
                setOrderData(res.data);
                setOrderPlaced(true);

                // Open WhatsApp with order details
                const whatsappUrl = sendWhatsAppOrder(res.data);
                window.open(whatsappUrl, '_blank');

                // Clear cart
                clearCart();
            }
        } catch (error) {
            console.error('Error placing order:', error);
            alert('Failed to place order. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (cartItems.length === 0 && !orderPlaced) {
        navigate('/shop');
        return null;
    }

    if (orderPlaced && orderData) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header />
                <div className="container mx-auto px-4 py-16">
                    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-12 h-12 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h1>
                        <p className="text-gray-600 mb-6">Thank you for your order. We'll contact you shortly via WhatsApp.</p>

                        <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                            <h2 className="font-semibold text-lg mb-3">Order Details</h2>
                            <div className="space-y-2 text-sm">
                                <p><span className="text-gray-600">Order Number:</span> <span className="font-medium">{orderData.orderNumber}</span></p>
                                <p><span className="text-gray-600">Total Amount:</span> <span className="font-medium text-primary">₹{orderData.totalAmount.toLocaleString('en-IN')}</span></p>
                                <p><span className="text-gray-600">Status:</span> <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">Pending Confirmation</span></p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/shop')}
                                className="w-full bg-primary hover:bg-primary-strong text-white py-3 rounded-lg font-medium transition-colors"
                            >
                                Continue Shopping
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="w-full border border-gray-300 hover:bg-gray-50 py-3 rounded-lg transition-colors"
                            >
                                Back to Home
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Checkout Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 md:p-8">
                            <h2 className="text-xl font-bold mb-6">Customer Information</h2>

                            <div className="space-y-5">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User className="w-4 h-4 inline mr-1" />
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="customerName"
                                        value={formData.customerName}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.customerName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter your full name"
                                    />
                                    {errors.customerName && (
                                        <p className="text-red-600 text-sm mt-1">{errors.customerName}</p>
                                    )}
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Phone className="w-4 h-4 inline mr-1" />
                                        Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        name="customerPhone"
                                        value={formData.customerPhone}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.customerPhone ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="10-digit mobile number"
                                    />
                                    {errors.customerPhone && (
                                        <p className="text-red-600 text-sm mt-1">{errors.customerPhone}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Mail className="w-4 h-4 inline mr-1" />
                                        Email Address (Optional)
                                    </label>
                                    <input
                                        type="email"
                                        name="customerEmail"
                                        value={formData.customerEmail}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.customerEmail ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="your.email@example.com"
                                    />
                                    {errors.customerEmail && (
                                        <p className="text-red-600 text-sm mt-1">{errors.customerEmail}</p>
                                    )}
                                </div>

                                {/* Address */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <MapPin className="w-4 h-4 inline mr-1" />
                                        Delivery Address *
                                    </label>
                                    <textarea
                                        name="customerAddress"
                                        value={formData.customerAddress}
                                        onChange={handleChange}
                                        rows="3"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.customerAddress ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter your complete delivery address"
                                    />
                                    {errors.customerAddress && (
                                        <p className="text-red-600 text-sm mt-1">{errors.customerAddress}</p>
                                    )}
                                </div>

                                {/* Notes */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Additional Notes (Optional)
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows="2"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="Any special instructions or requirements"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full mt-6 bg-primary hover:bg-primary-strong text-white py-4 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Placing Order...' : 'Place Order via WhatsApp'}
                            </button>

                            <p className="text-sm text-gray-500 text-center mt-4">
                                By placing this order, you agree to our terms and conditions
                            </p>
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <ShoppingBag className="w-5 h-5" />
                                Order Summary
                            </h2>

                            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                                {cartItems.map((item) => (
                                    <div key={item.id} className="flex gap-3 pb-3 border-b">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-16 h-16 object-cover rounded"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm line-clamp-2">{item.name}</p>
                                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                                            <p className="text-sm font-semibold text-primary">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-2 pt-4 border-t">
                                <div className="flex justify-between text-gray-600">
                                    <span>Subtotal</span>
                                    <span>₹{getCartTotal().toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Shipping</span>
                                    <span className="text-green-600">FREE</span>
                                </div>
                                <div className="flex justify-between text-xl font-bold pt-2 border-t">
                                    <span>Total</span>
                                    <span className="text-primary">₹{getCartTotal().toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Checkout;
